import { Empty, notification, Spin, Modal, Button, Form, Input, Select, Upload, Tag, Space } from "antd";
import { UploadOutlined, PlusOutlined, QrcodeOutlined, ShareAltOutlined } from "@ant-design/icons";
import esES from 'antd/lib/locale/es_ES';
import { CommonSearchForm, InternalPageLayout, Table } from "components";
import { useEffect, useState } from "react";
import { goodlookeroColumns } from "utils/tableColumns";
import { collectionGroup, getDocs, collection, addDoc, updateDoc, doc } from "firebase/firestore";
import { getApp } from "firebase/app";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { signInAnonymously } from "firebase/auth";
import { toDataURL } from "qrcode";
import { db, auth } from "config/firebase";

interface GoodLookero {
    id: string;
    Nombre: string;
    Categoria: string;
    Activo: boolean;
    Foto: string;
    Ciudades: string[];
    docRef?: any;
}

const { Option } = Select;

const ValidateGoodLooker = () => {
    const [search, setSearch] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [goodlookeros, setGoodlookeros] = useState<GoodLookero[]>([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingGoodLookero, setEditingGoodLookero] = useState<GoodLookero | null>(null);
    const [cities, setCities] = useState<string[]>([]);
    const [cityInput, setCityInput] = useState("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [qrModalVisible, setQrModalVisible] = useState(false);
    const [qrLink, setQrLink] = useState("");
    const [qrDataUrl, setQrDataUrl] = useState("");
    const [isQrGenerating, setIsQrGenerating] = useState(false);

    const [form] = Form.useForm();

    const getGoodlookeros = async (): Promise<GoodLookero[]> => {
        try {
            const snapshot = await getDocs(collectionGroup(db, "goodlookeros"));
            return snapshot.docs.map((snapshotDoc) => ({
                id: snapshotDoc.id,
                docRef: snapshotDoc.ref,
                ...snapshotDoc.data()
            })) as GoodLookero[];
        } catch (error) {
            console.error("Error obteniendo goodlookeros:", error);
            notification.error({
                message: "Error",
                description: "No se pudieron cargar los goodlookeros"
            });
            return [];
        }
    };

    const loadGoodlookeros = async () => {
        setIsLoading(true);
        const data = await getGoodlookeros();
        setGoodlookeros(data);
        setIsLoading(false);
    };

    useEffect(() => {
        loadGoodlookeros();
    }, []);

    const filterGoodlookeros = () => {
        if (!search) return goodlookeros;
        return goodlookeros.filter((item: GoodLookero) => 
            item.Nombre?.toLowerCase().includes(search.toLowerCase()) ||
            item.Categoria?.toLowerCase().includes(search.toLowerCase()) ||
            item.Ciudades?.some((city: string) => city.toLowerCase().includes(search.toLowerCase()))
        );
    };

    const getFilteredAndSorted = () => {
        const filtered = filterGoodlookeros();
        if (filtered.length === 0) return [];
        return filtered.sort((a: GoodLookero, b: GoodLookero) => 
            (a.Nombre || '').localeCompare(b.Nombre || '')
        );
    };

    const openAddModal = () => {
        form.resetFields();
        setEditingGoodLookero(null);
        setCities([]);
        setCityInput("");
        setSelectedFile(null);
        setModalVisible(true);
    };

    const openEditModal = (item: GoodLookero) => {
        form.setFieldsValue({
            Nombre: item.Nombre,
            Categoria: item.Categoria,
            Activo: item.Activo,
            Foto: item.Foto,
        });
        setCities(Array.isArray(item.Ciudades) ? item.Ciudades : []);
        setCityInput("");
        setSelectedFile(null);
        setEditingGoodLookero(item);
        setModalVisible(true);
    };

    const getValidationUrl = (id: string) => {
        return process.env.NODE_ENV === "production"
            ? `https://goodlookcorp.com/goodlookeros/validate/${id}`
            : `https://web-test.goodlook.com.mx/goodlookeros/validate/${id}`;
    };

    const openQrModal = async (item: GoodLookero) => {
        const link = getValidationUrl(item.id);
        setQrLink(link);
        setQrModalVisible(true);
        setIsQrGenerating(true);
        try {
            const dataUrl = await toDataURL(link, { width: 260, margin: 2 });
            setQrDataUrl(dataUrl);
        } catch (error) {
            console.error("Error generando QR:", error);
            notification.error({
                message: "Error",
                description: "No se pudo generar el código QR"
            });
        } finally {
            setIsQrGenerating(false);
        }
    };

    const closeQrModal = () => {
        setQrModalVisible(false);
        setQrLink("");
        setQrDataUrl("");
        setIsQrGenerating(false);
    };


    const handleNativeShare = async () => {
        if ((navigator as any).share) {
            try {
                await (navigator as any).share({
                    title: "QR GoodLookero",
                    text: `Validar goodlooker: ${qrLink}`,
                    url: qrLink
                });
            } catch (error) {
                console.error("Compartir nativo falló:", error);
            }
        }
    };

    const closeModal = () => {
        setModalVisible(false);
        setEditingGoodLookero(null);
        setCities([]);
        setCityInput("");
        setSelectedFile(null);
        form.resetFields();
    };

    const handleCityAdd = () => {
        const value = cityInput.trim();
        if (!value) return;
        if (cities.includes(value)) {
            notification.warning({
                message: "Ciudad duplicada",
                description: "Esa ciudad ya está agregada"
            });
            return;
        }
        setCities(prev => [...prev, value]);
        setCityInput("");
    };

    const handleCityRemove = (city: string) => {
        setCities(prev => prev.filter((item) => item !== city));
    };

    const handleFileSelect = (file: File) => {
        setSelectedFile(file);
        return false;
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (cities.length === 0) {
                notification.warning({
                    message: "Ciudades",
                    description: "Agrega al menos una ciudad"
                });
                return;
            }

            setIsSaving(true);
            if (!auth.currentUser) {
                await signInAnonymously(auth).catch((error) => {
                    console.error("Firebase anonymous auth failed", error);
                });
            }
            const payload: any = {
                Nombre: values.Nombre.trim(),
                Categoria: values.Categoria.trim(),
                Activo: values.Activo,
                Ciudades: cities,
                Foto: values.Foto?.trim() || editingGoodLookero?.Foto || ""
            };

            if (selectedFile) {
                const app = getApp();
                const storage = getStorage(app);
                const destination = `goodlookeros/${Date.now()}-${selectedFile.name}`;
                const fileRef = storageRef(storage, destination);
                await uploadBytes(fileRef, selectedFile);
                payload.Foto = await getDownloadURL(fileRef);
            }

            if (editingGoodLookero) {
                const targetDoc = editingGoodLookero.docRef || doc(db, "goodlookeros", editingGoodLookero.id);
                await updateDoc(targetDoc, payload);
                notification.success({
                    message: "GoodLookero actualizado",
                    description: "Los cambios se guardaron correctamente"
                });
            } else {
                await addDoc(collection(db, "goodlookeros"), payload);
                notification.success({
                    message: "GoodLookero agregado",
                    description: "Se creó el registro correctamente"
                });
            }
            closeModal();
            await loadGoodlookeros();
        } catch (error: any) {
            console.error(error);
            notification.error({
                message: "Error",
                description: error?.message || "No se pudo guardar el goodlookeros"
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <InternalPageLayout title="Auth Goodlookeros">
            <Spin spinning={isLoading}>
                <CommonSearchForm
                    clickAdd={openAddModal}
                    search={search}
                    onSearch={({target}: any) => setSearch(target.value.trimStart())}
                />
                <Table
                    localeProv={esES}
                    size="small"
                    rowKey={'id'}
                    columns={goodlookeroColumns(openEditModal, openQrModal)}
                    dataSource={getFilteredAndSorted()}
                    locale={{
                        triggerDesc: 'Ordenar de forma descendente',
                        triggerAsc: 'Ordenar de forma ascendente',
                        cancelSort: 'Cancelar ordenamiento',
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="No hay datos"
                            />
                        )
                    }}
                    scroll={{ x: 'auto' }}
                    pagination={{ defaultPageSize: 10 }}
                    loading={isLoading}
                />
            </Spin>

            <Modal
                title={editingGoodLookero ? "Editar GoodLookero" : "Agregar GoodLookero"}
                visible={modalVisible}
                onCancel={closeModal}
                onOk={handleSubmit}
                confirmLoading={isSaving}
                okText="Guardar"
                cancelText="Cancelar"
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ Activo: true }}
                >
                    <Form.Item
                        label="Nombre"
                        name="Nombre"
                        rules={[{ required: true, message: 'Ingresa un nombre' }]}
                    >
                        <Input placeholder="Nombre completo" />
                    </Form.Item>

                    <Form.Item
                        label="Categoría"
                        name="Categoria"
                        rules={[{ required: true, message: 'Ingresa una categoría' }]}
                    >
                        <Input placeholder="PREMIUM / STANDARD" />
                    </Form.Item>

                    <Form.Item
                        label="Estatus"
                        name="Activo"
                        rules={[{ required: true, message: 'Selecciona el estatus' }]}
                    >
                        <Select>
                            <Option value={true}>Activo</Option>
                            <Option value={false}>Inactivo</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item label="Foto (URL)" name="Foto">
                        <Input placeholder="Referencia de imagen en Firebase Storage" />
                    </Form.Item>

                    <Form.Item label="Imagen nueva">
                        <Upload
                            beforeUpload={(file) => handleFileSelect(file)}
                            onRemove={() => {
                                setSelectedFile(null);
                                return true;
                            }}
                            showUploadList={false}
                            accept="image/*"
                        >
                            <Button icon={<UploadOutlined />}>Seleccionar archivo</Button>
                        </Upload>
                        {selectedFile && (
                            <div style={{ marginTop: 8 }}>
                                {selectedFile.name}
                            </div>
                        )}
                        {!selectedFile && editingGoodLookero?.Foto && (
                            <div style={{ marginTop: 12 }}>
                                <img
                                    src={editingGoodLookero.Foto}
                                    alt="preview"
                                    style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8 }}
                                />
                            </div>
                        )}
                    </Form.Item>

                    <Form.Item label="Ciudades">
                        <Input
                            placeholder="Agregar ciudad y presionar Enter"
                            value={cityInput}
                            onChange={(e) => setCityInput(e.target.value)}
                            onPressEnter={(e) => {
                                e.preventDefault();
                                handleCityAdd();
                            }}
                            addonAfter={
                                <Button type="text" icon={<PlusOutlined />} onClick={handleCityAdd}>
                                    Agregar
                                </Button>
                            }
                        />
                        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {cities.map((city) => (
                                <Tag key={city} closable onClose={() => handleCityRemove(city)}>
                                    {city}
                                </Tag>
                            ))}
                        </div>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="QR GoodLookero"
                visible={qrModalVisible}
                onCancel={closeQrModal}
                footer={[
                    <Button key="close" onClick={closeQrModal}>
                        Cerrar
                    </Button>,
                    <Button
                        key="share"
                        icon={<ShareAltOutlined />}
                        type="primary"
                        onClick={handleNativeShare}
                        disabled={!(navigator as any).share}
                    >
                        Compartir
                    </Button>
                ]}
            >
                <div style={{ textAlign: 'center' }}>
                    {isQrGenerating ? (
                        <Spin />
                    ) : (
                        <>
                            {qrDataUrl && (
                                <img
                                    src={qrDataUrl}
                                    alt="QR GoodLookero"
                                    style={{ width: 260, height: 260, marginBottom: 16 }}
                                />
                            )}
                            <p style={{ marginBottom: 8, wordBreak: 'break-all' }}>{qrLink}</p>
                            <Space>
                                <Button
                                    icon={<QrcodeOutlined />}
                                    onClick={() => {
                                        navigator.clipboard.writeText(qrLink);
                                        notification.success({
                                            message: 'Copiado',
                                            description: 'Enlace de validación copiado al portapapeles'
                                        });
                                    }}
                                >
                                    Copiar enlace
                                </Button>
                            </Space>
                        </>
                    )}
                </div>
            </Modal>
        </InternalPageLayout>
    );
};

export default ValidateGoodLooker;
